import { type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface ModernBreadcrumb {
  label: string;
  href?: string;
}

/**
 * ModernPageHeader — one consistent page header for every route. Quiet by
 * design: title + optional eyebrow/breadcrumbs on the left, actions on the
 * right. No page-specific header styling anywhere else.
 */
export function ModernPageHeader({
  title,
  description,
  eyebrow,
  breadcrumbs,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  breadcrumbs?: ModernBreadcrumb[];
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-3 pb-1', className)}>
      <div className="min-w-0">
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {breadcrumbs.map((crumb, i) => (
              <span key={`${crumb.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-border">/</span>}
                <span className={cn(i === breadcrumbs.length - 1 && 'text-foreground')}>
                  {crumb.label}
                </span>
              </span>
            ))}
          </nav>
        ) : (
          eyebrow && (
            <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {eyebrow}
            </div>
          )
        )}
        <h1 className="truncate text-lg font-semibold tracking-[-0.01em] text-foreground">
          {title}
        </h1>
        {description && (
          <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
